import {cn} from '@/lib/utils';
import {X} from 'lucide-react';
import {AnimatePresence, motion} from 'motion/react';
import {
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Used as the modal's `aria-label` and, by default, rendered as a plain
   * `<h2>` heading in the header. Pass `headerContent` to replace the
   * heading with a custom layout (logo, eyebrow, etc.).
   */
  title: string;
  /** Optional rich header content; replaces the default `<h2>{title}</h2>`. */
  headerContent?: ReactNode;
  /** Additional classes for the modal card */
  className?: string;
}

/**
 * Animated full-screen modal with backdrop, close button, and title.
 * Follows the pattern used by SearchFiltersModal and OrganizerModal.
 * Traps focus within the modal and closes on Escape.
 */
export const Modal: FC<PropsWithChildren<ModalProps>> = ({
  open,
  onOpenChange,
  title,
  headerContent,
  className,
  children,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // Auto-focus the modal card when it opens
  useEffect(() => {
    if (open && cardRef.current) {
      cardRef.current.focus();
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              onOpenChange(false);
            }
          }}
        >
          {/* Backdrop */}
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2}}
            className="absolute inset-0 bg-black/50"
            onClick={() => onOpenChange(false)}
          />

          {/* Modal card */}
          <motion.div
            ref={cardRef}
            tabIndex={-1}
            initial={{opacity: 0, scale: 0.95, y: 10}}
            animate={{opacity: 1, scale: 1, y: 0}}
            exit={{opacity: 0, scale: 0.95, y: 10}}
            transition={{duration: 0.2, ease: 'easeOut'}}
            className={cn(
              'relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col outline-none',
              className
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-4 px-6 py-4 border-b">
              {headerContent ?? (
                <h2 className="text-base font-semibold font-sans">{title}</h2>
              )}
              <button
                onClick={() => onOpenChange(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer flex-shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
